import { combineEpics } from 'redux-observable';
import { filter, map, withLatestFrom } from 'rxjs';
import { clamp, cloneDeep } from 'lodash';

import module from './index';
import utils from './canvases.utils';

import stage from 'domains/stage';
import products from 'domains/products';
import Arrangement from 'domains/arrangement';

import { selectZoomedPPI } from 'selectors/ppi';

import { notNull } from 'utils/type_guards';

import type { Epic } from 'redux-observable';
import type { Action } from '@reduxjs/toolkit';
import type { RootState } from 'store/wos_store';

const { actions, selectors } = module;

/**
 * If the active product changes to a standard size, and we have custom size data, clear it
 */
const tearDownCustomSizeEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.changeActiveCanvasConfig.product.match),
    withLatestFrom(state$),
    map(([action, state]) => {
      const product = products.selectors.selectById(state, action.payload);
      if (!product || products.utils.isCustomSizeProduct(product)) return;

      return state;
    }),
    filter(notNull),
    map(() => {
      return {
        customPrice: undefined,
        customOptionPrices: undefined,
        height: undefined,
        width: undefined,
      };
    }),
    map(actions.updateCanvas)
  );

export const prepareCreateCanvasEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.createCanvas.match),
    withLatestFrom(state$),
    map(([{ payload }, state]) => {
      const activeId = selectors.activeId(state);
      const position = { x: 0, y: 0 };
      const canvases = selectors.allWithProducts(state);

      if (canvases.length > 0) {
        position.x = Arrangement.createWithProducts(canvases).width + 1;
      }

      let config = payload.config || {};

      if (payload.duplicate && activeId) {
        config = {
          ...config,
          ...cloneDeep(selectors.selectById(state, activeId)),
        };
      }

      return {
        rotation: 0,
        userRotated: false,
        ...config,
        // Important that these two properties overwrite anything from previous configs
        position,
        id: utils.idgen(),
      };
    }),
    map(actions.addOne)
  );

export const moveArrangementEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.moveArrangement.match),
    withLatestFrom(state$),
    map(([{ payload }, state]) => {
      let { dx, dy } = payload;
      const ppi = selectZoomedPPI(state);
      const currentPosition = selectors.arrangementPosition(state);

      // Movement amounts are in pixels, arrangement position is in inches
      const nextPosition = {
        x: currentPosition.x + dx / ppi,
        y: currentPosition.y + dy / ppi,
      };

      const limits = stage.selectors.wallPreviewBounds(state);

      if (limits) {
        const withProducts = selectors.allWithProducts(state);
        const arrangement = Arrangement.createWithProducts(withProducts);

        let { leftLimit, rightLimit, topLimit, bottomLimit } = limits;

        // Wall preview dimensions are in pixels, arrangement position is in inches
        leftLimit /= ppi;
        topLimit /= ppi;
        rightLimit /= ppi;
        bottomLimit /= ppi;

        // Determine the right/bottom bounds based on the entire arrangement size
        rightLimit -= arrangement.width;
        bottomLimit -= arrangement.height;

        nextPosition.x = clamp(nextPosition.x, leftLimit, rightLimit);
        nextPosition.y = clamp(nextPosition.y, topLimit, bottomLimit);
      }

      return nextPosition;
    }),
    map((position) => actions.setArrangementPosition(position))
  );

export const moveCanvasEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.moveCanvas.match),
    withLatestFrom(state$),
    map(([{ payload }, state]) => {
      const { id, next } = payload;
      const canvas = selectors.selectById(state, id);
      if (!canvas || !canvas.product) return;

      const { dx, dy } = payload;
      const ppi = selectZoomedPPI(state);

      const nextPosition = {
        x: canvas.position.x + dx / ppi,
        y: canvas.position.y + dy / ppi,
      };

      const limits = stage.selectors.wallPreviewBounds(state);

      if (limits) {
        const arrangementPosition = selectors.arrangementPosition(state);

        let { leftLimit, rightLimit, topLimit, bottomLimit } = limits;

        // Wall preview dimensions are in pixels, arrangement position is in inches
        leftLimit /= ppi;
        topLimit /= ppi;
        rightLimit /= ppi;
        bottomLimit /= ppi;

        const product = products.selectors.selectById(state, canvas.product);
        if (!product) return;

        // If we have next size, we're repositioning based on a product drag-resize
        const productSize = next || products.utils.calculateProductSize(product, canvas);

        // Determine bounds for canvas' relative position within the arrangement
        topLimit -= arrangementPosition.y;
        bottomLimit -= arrangementPosition.y + productSize.height;
        leftLimit -= arrangementPosition.x;
        rightLimit -= arrangementPosition.x + productSize.width;

        nextPosition.x = clamp(nextPosition.x, leftLimit, rightLimit);
        nextPosition.y = clamp(nextPosition.y, topLimit, bottomLimit);
      }

      return { ...nextPosition, id };
    }),
    filter(notNull),
    map((position) => actions.setCanvasPosition(position))
  );

const combined = combineEpics(
  tearDownCustomSizeEpic,
  prepareCreateCanvasEpic,
  moveArrangementEpic,
  moveCanvasEpic
);
export default combined;
